import type { ConsoleBrandRow, ConsoleOverview } from '@rsecure/brand-contracts/console';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  Badge,
  Empty,
  ErrorState,
  Icon,
  Loading,
  LogoThumb,
  Ltr,
  Pager,
  StatusBadge,
  Time,
} from '../components/ui';
import { api } from '../lib/api';
import { fmtNum, fmtRelative } from '../lib/format';
import { useSearchText, useUrlState } from '../lib/url';

const ASSET_LABEL = {
  light_logo: 'Has a light-background logo',
  no_light_logo: 'Published without a light-background logo',
  any_logo: 'Any published logo',
  no_logo: 'No published logo',
} as const;
const REVIEW_LABEL = {
  open: 'Has open reviews',
  blocking: 'Blocked by a review',
  none: 'No open reviews',
} as const;
const SORT_LABEL = {
  name: 'Name',
  updated: 'Last updated',
  fetched: 'Last fetched',
  state: 'State',
  region: 'Region',
} as const;

const FILTER_KEYS = [
  'q',
  'publication',
  'state',
  'region',
  'category',
  'origin',
  'assets',
  'review',
] as const;

export function StatStrip({ o }: { o: ConsoleOverview }) {
  const failed = o.runs.failed24h;
  return (
    <section className="stats" aria-label="Catalog statistics">
      <div className="stat">
        <div className="label">Candidate records</div>
        <Link className="value" to="/candidates">
          {fmtNum(o.candidates.total)}
        </Link>
        <div className="sub">{fmtNum(o.candidates.linked)} linked to an entity</div>
      </div>
      <div className="stat">
        <div className="label">Canonical entities</div>
        <Link className="value" to="/">
          {fmtNum(o.entities.total)}
        </Link>
        <div className="sub">
          {Object.entries(o.entities.byState)
            .filter(([s]) => s !== 'PUBLISHED')
            .map(([s, n]) => `${fmtNum(n)} ${s.toLowerCase()}`)
            .join(' · ') || 'all published'}
        </div>
      </div>
      <div className="stat">
        <div className="label">Published brands</div>
        <Link className="value" to="/?publication=published">
          {fmtNum(o.entities.published)}
        </Link>
        <div className="sub">
          {fmtNum(o.revisions.total)} revisions · {fmtNum(o.entities.publishedWithoutLightLogo)}{' '}
          without light logo
        </div>
      </div>
      <div className="stat tone-warn">
        <div className="label">Require ingestion</div>
        <Link className="value" to="/candidates?status=needs_fetch">
          {fmtNum(o.candidates.needsIngestion)}
        </Link>
        <div className="sub">candidates with no provider data yet</div>
      </div>
      <div className="stat tone-warn">
        <div className="label">Awaiting review</div>
        <Link className="value" to="/?review=blocking">
          {fmtNum(o.entities.withBlockingReviews)}
        </Link>
        <div className="sub">blocked entities · {fmtNum(o.reviews.open)} open reviews</div>
      </div>
      <div className={`stat ${failed ? 'tone-bad' : ''}`}>
        <div className="label">Failed ingestion runs</div>
        <Link className="value" to="/operations?status=failed">
          {fmtNum(failed)}
        </Link>
        <div className="sub">last 24h · {fmtNum(o.runs.failedTotal)} all time</div>
      </div>
      <div className="stat">
        <div className="label">Last successful sync</div>
        <div
          className="value"
          style={{ fontSize: 17, paddingTop: 5 }}
          title={o.lastSuccessfulSync ?? ''}
        >
          {o.lastSuccessfulSync ? fmtRelative(o.lastSuccessfulSync, Date.now()) : 'never'}
        </div>
        <div className="sub">succeeded fetch or publish run</div>
      </div>
    </section>
  );
}

export function CatalogPage() {
  const navigate = useNavigate();
  const { sp, set, reset } = useUrlState();
  const [text, setText] = useSearchText();
  const limit = Number(sp.get('limit') ?? 25);
  const offset = Number(sp.get('offset') ?? 0);
  const sort = sp.get('sort') ?? 'name';
  const dir = sp.get('dir') ?? 'asc';

  const overview = useQuery({ queryKey: ['overview'], queryFn: api.overview, staleTime: 30_000 });
  const params = Object.fromEntries(
    [...FILTER_KEYS, 'sort', 'dir', 'limit', 'offset'].map((k) => [k, sp.get(k)]),
  );
  const brands = useQuery({
    queryKey: ['brands', params],
    queryFn: () => api.brands(params),
    placeholderData: keepPreviousData,
  });
  const facets = overview.data?.facets;
  const active = FILTER_KEYS.filter((k) => k !== 'q' && sp.get(k));

  const select = (key: string, label: string, options: Array<{ value: string; label: string }>) => (
    <label>
      <span className="sr-only">{label}</span>
      <select
        className="select"
        aria-label={label}
        value={sp.get(key) ?? ''}
        onChange={(e) => set({ [key]: e.target.value || null })}
      >
        <option value="">{label}: All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
  const facetOpts = (list?: Array<{ value: string | null; count: number }>) =>
    (list ?? [])
      .filter((f) => f.value)
      .map((f) => ({ value: f.value as string, label: `${f.value} (${fmtNum(f.count)})` }));

  const sortHeader = (key: string, label: string) => (
    <button
      type="button"
      onClick={() => set({ sort: key, dir: sort === key && dir === 'asc' ? 'desc' : 'asc' }, false)}
      aria-label={`Sort by ${label}`}
    >
      {label}
      {sort === key && <Icon name={dir === 'asc' ? 'up' : 'down'} />}
    </button>
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Brand catalog</h1>
          <p>
            Canonical brand entities in the V3 catalog. Candidates are input rows; entities are
            verified identities; revisions are immutable published snapshots.
          </p>
        </div>
      </div>
      {overview.data ? (
        <StatStrip o={overview.data} />
      ) : overview.isError ? (
        <ErrorState error={overview.error} onRetry={() => overview.refetch()} />
      ) : (
        <div className="stats">
          {Array.from({ length: 7 }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: placeholder
            <div key={i} className="stat skeleton" style={{ height: 92 }} />
          ))}
        </div>
      )}

      <search className="filters">
        <div className="search">
          <Icon name="search" />
          <input
            className="input"
            type="search"
            placeholder="Search name, localized name, slug, domain or candidate name"
            aria-label="Search name, localized name, slug, domain or candidate name"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        {select('publication', 'Publication', [
          { value: 'published', label: 'Published' },
          { value: 'unpublished', label: 'Not published' },
        ])}
        {select('state', 'Pipeline state', facetOpts(facets?.states))}
        {select('region', 'Region', facetOpts(facets?.regions))}
        {select('category', 'Category', facetOpts(facets?.categories))}
        {select('origin', 'Source', facetOpts(facets?.origins))}
        {select(
          'assets',
          'Assets',
          (['light_logo', 'no_light_logo', 'any_logo', 'no_logo'] as const).map((v) => ({
            value: v,
            label: ASSET_LABEL[v],
          })),
        )}
        {select(
          'review',
          'Review',
          (['open', 'blocking', 'none'] as const).map((v) => ({
            value: v,
            label: REVIEW_LABEL[v],
          })),
        )}
        <label>
          <span className="sr-only">Sort</span>
          <select
            className="select"
            aria-label="Sort"
            value={sort}
            onChange={(e) => set({ sort: e.target.value }, false)}
          >
            {(['name', 'updated', 'fetched', 'state', 'region'] as const).map((s) => (
              <option key={s} value={s}>
                Sort: {SORT_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        {(active.length > 0 || text) && (
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => {
              setText('');
              reset();
            }}
          >
            Clear filters
          </button>
        )}
      </search>

      <section className="card" aria-label="Brands">
        {brands.isError ? (
          <ErrorState error={brands.error} onRetry={() => brands.refetch()} />
        ) : !brands.data ? (
          <Loading rows={10} />
        ) : brands.data.total === 0 ? (
          overview.data?.entities.total === 0 ? (
            <Empty
              title="The catalog has no entities yet"
              sub="Candidates exist but none has been resolved to a canonical entity. Run ingestion or the historical backfill."
            />
          ) : (
            <Empty title="No brands match these filters" />
          )
        ) : (
          <>
            <div className="table-wrap" aria-busy={brands.isFetching}>
              <table className="t">
                <thead>
                  <tr>
                    <th scope="col">{sortHeader('name', 'Brand')}</th>
                    <th scope="col">Primary domain</th>
                    <th scope="col">{sortHeader('region', 'Category · Region')}</th>
                    <th scope="col" className="hide-sm hide-md">
                      Source
                    </th>
                    <th scope="col">{sortHeader('state', 'Status')}</th>
                    <th scope="col" className="hide-sm">
                      Active revision
                    </th>
                    <th scope="col" className="hide-sm">
                      Reviews
                    </th>
                    <th scope="col" className="hide-sm hide-md">
                      {sortHeader('fetched', 'Last fetched')}
                    </th>
                    <th scope="col">{sortHeader('updated', 'Updated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {brands.data.items.map((b) => (
                    <BrandRow key={b.id} b={b} onOpen={() => navigate(`/brands/${b.slug}`)} />
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              total={brands.data.total}
              limit={limit}
              offset={offset}
              onChange={(o) => set({ offset: String(o) }, false)}
              onLimit={(l) => set({ limit: String(l) })}
            />
          </>
        )}
      </section>
    </>
  );
}

function BrandRow({ b, onOpen }: { b: ConsoleBrandRow; onOpen: () => void }) {
  return (
    <tr className="row-link" onClick={onOpen} data-testid="brand-row" data-slug={b.slug}>
      <td>
        <div className="cell-brand">
          <LogoThumb url={b.logo?.url} name={b.name} />
          <div className="names">
            <Link className="n1" to={`/brands/${b.slug}`} onClick={(e) => e.stopPropagation()}>
              {b.name}
            </Link>
            <div className="n2">
              {b.nameHe && (
                <>
                  <bdi dir="auto">{b.nameHe}</bdi>
                  {' · '}
                </>
              )}
              <Ltr mono>{b.slug}</Ltr>
              {b.candidateNames.length > 0 && (
                <span title="Original candidate names that differ from the canonical name">
                  {' '}
                  · candidate: {b.candidateNames.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td>
        {b.primaryDomain ? (
          <span title={`verification: ${b.primaryDomain.verification}`}>
            <Ltr>{b.primaryDomain.domain}</Ltr>
            {b.primaryDomain.verification !== 'verified' && (
              <>
                {' '}
                <Badge tone="warn">{b.primaryDomain.verification}</Badge>
              </>
            )}
          </span>
        ) : (
          <span className="faint">—</span>
        )}
      </td>
      <td>
        <div>{b.category ?? <span className="faint">—</span>}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {b.region}
        </div>
      </td>
      <td className="hide-sm hide-md">
        <span className="muted nowrap">{b.origin.replace(/_/g, ' ')}</span>
      </td>
      <td>
        <StatusBadge kind="pipeline" value={b.pipelineState} />
      </td>
      <td className="hide-sm">
        {b.activeRevision ? (
          <span className="num">#{b.activeRevision.number}</span>
        ) : (
          <span className="faint">—</span>
        )}
      </td>
      <td className="hide-sm">
        {b.blockingReviews > 0 ? (
          <Badge tone="bad">{b.blockingReviews} blocking</Badge>
        ) : b.openReviews > 0 ? (
          <Badge tone="warn">{b.openReviews} open</Badge>
        ) : (
          <span className="faint">—</span>
        )}
      </td>
      <td className="hide-sm hide-md">
        <Time iso={b.lastFetchedAt} relative />
      </td>
      <td>
        <Time iso={b.updatedAt} relative />
      </td>
    </tr>
  );
}
